import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import { getStatusLabel } from "@store/utilityFunctions";
import { useToast } from "@components/Toast/ToastContext";
import useApiResource from "./useApiResource";
import { stageIndex } from "./config/stages";
import DetailHeader from "./detail/DetailHeader";
import StageStepper from "./detail/StageStepper";
import ConfirmModal from "./detail/ConfirmModal";
import { ApproveTicketModal } from "./detail/ApprovalModals";
// import { DeclineTicketModal } from "./detail/ApprovalModals";
// import OutcomeChips from "./detail/OutcomeChips";
import { BriefCard } from "./detail/SidePanels";
import TabBar from "./detail/TabBar";
import { SaveBar } from "./detail/FormBits";
import useTicketDraft from "./detail/useTicketDraft";
import ProductTab from "./detail/tabs/ProductTab";
import DeliveryTab from "./detail/tabs/DeliveryTab";
import ServiceTab from "./detail/tabs/ServiceTab";
import ReportTab from "./detail/tabs/ReportTab";
import NoteTab, { canSeeNoteTab } from "./detail/tabs/NoteTab";
import ActivityLogTab from "./detail/tabs/ActivityLogTab";
import { TnSkeletonRows } from "./components/TnSkeleton";
import "./TicketNew.css";
import "./TicketCaseDetail.css";

const TAB_PANELS = {
  product: ProductTab,
  delivery: DeliveryTab,
  service: ServiceTab,
  report: ReportTab,
  note: NoteTab,
  activity: ActivityLogTab,
};

/*
RMA Portal - case detail.

GET /ticket/{id}/ backs the header, brief card, Report tab and activity log
below. The stage badge/stepper is derived from that response's `stage` label
and resyncs on every load. The target status comes from the detail response's
flow fields, not from ticket_status_list - that list mixes in reject/cancel
branches ("... / Completed" is followed by "Not Approved"):
Advance/Revert PATCH the ticket's real status via ticket/{id}/status/ (same
endpoint as handleServiceNext in
Warranties/SingleWarranty/TicketProcess/TicketProcess.js), then reload:
- Advance sends `next_statuses[0]`; disabled when the array is empty (closed,
  last step, or status not in this ticket's flow). RMAS-128 adds
  POST ticket/{id}/advance/ (apiEndpoints.ticket.advance) for this, but the
  backend hasn't shipped it yet - switch over once it has.
- Revert sends `previous_status`; disabled when it's null (first step, a
  reject/cancel branch, or status not in the flow).
- The PATCH doesn't validate transitions, and re-sending the current status
  still writes a history row, so it's skipped when the target equals the
  current status.
- `is_closed` marks a final status. Use it, not `stage`, for "is this case
  done": "Replacement Sent" and "Repair complete, goods sent out" report stage
  Complete but can still advance to "Completed" / "Service Completed".
While the status is "... Pending Approval" (the first status after a ticket is
created) Advance stage approves the ticket (PUT ticket/{id}/approved/) for
APPROVAL_ROLES, mirroring the approval footer in that same TicketProcess.js -
see detail/ApprovalModals.js for the request it makes. Decline is out of scope
for now, so Revert stays in its place.
Sub-stage still has no endpoint (see config/stages.js).
Save (PATCH /ticket/{id}/, RMAS-128): the Product and Delivery tabs edit one
page-level draft (detail/useTicketDraft.js), so edits survive switching tabs
and the SaveBar sends every changed field in one request; its response is
rendered directly. Advancing doesn't need a Save first and keeps the draft.
On the Service tab only Service Type is editable, saved straight away rather
than through the draft (see ServiceTab.js). Store location and the return
address keep their own endpoints - see ProductTab.js.

Route params are read with useParams() rather than props.match: the app's
ProtectedRoute calls render(this.props) and never forwards router props.
*/
// Same role gate as the Next/Reject footer in
// Warranties/SingleWarranty/TicketProcess/TicketProcess.js.
const STAGE_ACTION_ROLES = ["Service", "Staff", "Service Center Admin", "Admin"];
// Same role gate as TicketProcess.js's Approve/Decline footer.
const APPROVAL_ROLES = ["Admin", "Approver", "Service Center Admin"];
// Same gates as Warranties/SingleWarranty: commercial roles get TicketProcess
// fully disabled, and contractors never see TicketProcess/DeliveryAddress.
const EDIT_BLOCKED_ROLES = ["Commercial Staff", "Commercial Admin", "Contractor"];

// Save answers with the full detail; fall back to a GET if a response ever
// comes back without it.
const applyTicketResponse = function (ticket, res) {
  if (res && res.id) ticket.setData(res);
  else ticket.reload();
};
const TicketCaseDetail = function (props) {
  const { protectedFetch, userInfo } = props;
  const { showToast } = useToast();
  const history = useHistory();
  const params = useParams();
  // The route param is the numeric ticket id (see TicketNew.js's openCase) -
  // not the human-readable ticket_number, which the API doesn't accept here.
  const reference = params && params.reference ? params.reference : null;

  const ticket = useApiResource(
    protectedFetch,
    reference ? apiEndpoints.ticket.detail(reference) : null,
    null,
    { skip: !reference }
  );
  const data = ticket.data;

  const [stage, setStage] = useState(2);
  const [subStage, setSubStage] = useState(0);
  const [tab, setTab] = useState("product");
  // const [outcome, setOutcome] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // null | "advance" | "revert"
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null); // null | "approve" | "decline"

  // Reflect the ticket's real status in the stepper on every load - the
  // initial GET and every reload that follows a confirmed Advance/Revert.
  useEffect(
    function () {
      if (!data) return;
      const index = stageIndex(data.stage);
      if (index !== -1) setStage(index);
      setSubStage(0);
    },
    [data]
  );

  // Every final status maps to stage Complete, but not every Complete-stage
  // status is final - see the header comment.
  const isComplete = !!(data && data.is_closed);
  const role = userInfo && userInfo.user && userInfo.user.role;
  const canAdvance = STAGE_ACTION_ROLES.includes(role);
  const canApprove = APPROVAL_ROLES.includes(role);
  const canEdit = !!(data && data.id) && !EDIT_BLOCKED_ROLES.includes(role);

  const draft = useTicketDraft(protectedFetch, data, function (res) {
    applyTicketResponse(ticket, res);
    showToast("Changes saved", "success");
  });

  const currentStatus = data && data.status;
  // Same check TicketProcess.js uses to show Approve/Decline.
  const isPendingApproval = String(currentStatus || "").includes("Pending Approval");

  const nextStatuses = (data && data.next_statuses) || [];
  const nextStatus = nextStatuses[0] || null;
  const prevStatus = (data && data.previous_status) || null;
  const atStart = !prevStatus;
  const atEnd = !nextStatus;
  // RMAS-137: show status_labels text in the UI, but PATCH the raw values above.
  const nextStatusLabel = nextStatus && getStatusLabel(data, nextStatus);
  const prevStatusLabel = prevStatus && getStatusLabel(data, prevStatus);

  const requestAdvance = function () {
    if (atEnd || savingStatus || isPendingApproval) return;
    setPendingAction("advance");
  };

  const requestRevert = function () {
    if (atStart || savingStatus) return;
    setPendingAction("revert");
  };

  const cancelPendingAction = function () {
    if (savingStatus) return;
    setPendingAction(null);
  };

  // PATCH the ticket's real status to next_statuses[0] / previous_status, then
  // reload so the stepper reflects what the backend actually confirmed.
  const confirmPendingAction = function () {
    const targetStatus = pendingAction === "advance" ? nextStatus : prevStatus;
    if (!targetStatus || targetStatus === currentStatus || !data || !data.id) {
      setPendingAction(null);
      return;
    }
    setSavingStatus(true);
    setStatusError(null);
    protectedFetch(
      apiEndpoints.ticket.status(data.id),
      "PATCH",
      { status: targetStatus },
      errorFunctions.checkResponseThenJson
    )
      .then(function () {
        setSavingStatus(false);
        setPendingAction(null);
        showToast("Ticket status updated", "success");
        ticket.reload();
      })
      .catch(function () {
        setSavingStatus(false);
        setStatusError("Couldn't update the ticket status. Please try again.");
      });
  };

  const closeApproval = function () {
    setApprovalAction(null);
  };

  const finishApproval = function (message, tone) {
    setApprovalAction(null);
    showToast(message, tone || "success");
    ticket.reload();
  };

  const hiddenTabs = canSeeNoteTab(userInfo) ? [] : ["note"];
  const Panel = (!hiddenTabs.includes(tab) && TAB_PANELS[tab]) || ProductTab;
  const showSkeleton = ticket.loading && !ticket.hasLoadedOnce;

  const modalCopy =
    pendingAction === "advance"
      ? {
          title: "Advance status",
          message: nextStatus
            ? 'Move this ticket to the next status "' + nextStatusLabel + '"?'
            : null,
          confirmLabel: "Advance",
        }
      : pendingAction === "revert"
      ? {
          title: "Revert status",
          message: prevStatus
            ? 'Move this ticket back to the previous status "' + prevStatusLabel + '"?'
            : null,
          confirmLabel: "Revert",
        }
      : null;

  return (
    <div className="ticket-new-root">
      <div className="tn-page">
        <DetailHeader
          reference={reference}
          ticketNumber={data && data.ticket_number}
          stage={stage}
          // outcome={outcome}
          onBack={function () {
            if (draft.isDirty && !window.confirm("Leave this case? Your unsaved changes will be lost.")) {
              return;
            }
            history.push("/ticket-new");
          }}
          onAdvance={requestAdvance}
          onRevert={requestRevert}
          atStart={atStart || savingStatus}
          atEnd={atEnd || savingStatus}
          canAdvance={canAdvance}
          pendingApproval={isPendingApproval}
          canApprove={canApprove && !!(data && data.id)}
          onApprove={function () {
            setApprovalAction("approve");
          }}
          busy={ticket.loading}
        />

        {ticket.error ? (
          <div className="tn-alert" role="alert">
            <span className="tn-alert-text">{ticket.error}</span>
            <button type="button" className="tn-alert-retry" onClick={ticket.reload}>
              Retry
            </button>
          </div>
        ) : null}

        {statusError ? (
          <div className="tn-alert" role="alert">
            <span className="tn-alert-text">{statusError}</span>
          </div>
        ) : null}

        {showSkeleton ? (
          <TnSkeletonRows n={6} />
        ) : (
          <React.Fragment>
            <StageStepper
              stage={stage}
              subStage={subStage}
              onSubStage={setSubStage}
              isComplete={isComplete}
            />

            {/* {isComplete ? (
              <OutcomeChips value={outcome} onChange={setOutcome} />
            ) : null} */}

            <div className="tn-detail-grid">
              <BriefCard stage={stage} ticket={data} />

              <div className="tn-detail-main">
                <TabBar value={tab} onChange={setTab} hidden={hiddenTabs} />
                <Panel
                  ticket={data}
                  protectedFetch={protectedFetch}
                  userInfo={userInfo}
                  reloadTicket={ticket.reload}
                  draft={draft}
                  canEdit={canEdit}
                />
                {canEdit ? (
                  <SaveBar
                    count={draft.dirtyKeys.length}
                    error={draft.error}
                    saving={draft.saving}
                    onSave={draft.save}
                    onDiscard={draft.discard}
                  />
                ) : null}
              </div>
            </div>
          </React.Fragment>
        )}
      </div>

      <ConfirmModal
        open={!!pendingAction}
        title={modalCopy && modalCopy.title}
        message={modalCopy && modalCopy.message}
        confirmLabel={modalCopy && modalCopy.confirmLabel}
        busy={savingStatus}
        onConfirm={confirmPendingAction}
        onCancel={cancelPendingAction}
      />

      {approvalAction === "approve" && data ? (
        <ApproveTicketModal
          ticket={data}
          nextStatus={nextStatusLabel}
          protectedFetch={protectedFetch}
          onDone={finishApproval}
          onClose={closeApproval}
        />
      ) : null}

      {/* Decline is out of scope for now - Revert shows in its place.
      {approvalAction === "decline" && data ? (
        <DeclineTicketModal
          ticket={data}
          protectedFetch={protectedFetch}
          onDone={finishApproval}
          onClose={closeApproval}
        />
      ) : null} */}
    </div>
  );
};

export default TicketCaseDetail;
